// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Taking a device off the machine, and everything that belonged to it (24 Oct 2026).
//
// `layoutDraft.remove` used to take a device's wiring by the palette's naming — `<id>Switch`,
// `<id>Coil` — which is right for a part placed from the palette and wrong for nearly every part a
// shipped machine came with: NEON NOVA's captive ball is `darkMatter` and its switch is also
// `darkMatter`. The web Builder tolerates a broken draft between edits, so nobody saw it there; the
// headset reloads the machine on every edit and must refuse one the loader would refuse, so on the
// Quest (1.0.205, 24 Oct) "remove does not work": the release at the bin was refused with *no device
// is declared with id "darkMatter"*. Removing every part of the six shipped machines in turn, 37 of
// 197 left a machine the validator accepted.
//
// **What belonged to the part goes with it, wherever it is named:**
// - every switch whose `device` is the part (and the palette's `<id>Switch`);
// - its coil — the one its `coil` member names, and the palette's `<id>Coil` — unless another
//   device still drives it;
// - every rule whose trigger is one of those switches: it can never fire again;
// - every action, in a rule or a mode's hooks, that drives one of those coils or locks a ball in
//   the part;
// - those switches in every mode's shot list (an emptied list goes, as `modes.ts` writes it);
// - its id in any drop-target bank, and the colliders that are its own (`device` is the part);
// - the rules the palette wrote for it (a magnet's power rule).
//
// What is left is the author's: a mode the removed part was the only way to start is a mode
// nothing can start, and the validator says so — the headset refuses that removal with the
// sentence, the canvas shows it in the overlay. One Undo brings every one of these back.
//
// Pure: no DOM, no clock. `Drafts.Remove` in the native `LayoutDraft.cs` is this, case for case,
// held by `core/fixtures/parity/device-removal-v1.json`.

import type { MachineDefinition } from '../../core/ts/machine'
import type { EditorPart } from './parts'
import { wiringFor } from './palette'

/** What went with a device, by id, in the order the definition held them. */
export interface DeviceRemoval {
  readonly definition: MachineDefinition
  readonly switches: readonly string[]
  readonly coils: readonly string[]
  readonly rules: readonly string[]
  readonly colliders: readonly string[]
  /** Banks the device was taken out of. */
  readonly banks: readonly string[]
  /** Modes whose shot list lost a switch. */
  readonly modes: readonly string[]
  /** Actions taken out of rules and mode hooks that remain. */
  readonly actions: number
}

type Rec = Record<string, unknown>

function rec(value: unknown): Rec {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Rec) : {}
}

function idOf(value: unknown): string | undefined {
  const id = rec(value)['id']
  return typeof id === 'string' ? id : undefined
}

function list(value: unknown): readonly unknown[] {
  return Array.isArray(value) ? value : []
}

/**
 * The machine without one device and everything that belonged to it. Returns the definition
 * unchanged, and nothing removed, when there is no such device.
 */
export function withoutDevice(definition: MachineDefinition, deviceId: string): DeviceRemoval {
  const root = definition as unknown as Rec
  const devices = list(root['devices'])
  const going = devices.find((one) => idOf(one) === deviceId)
  const none: DeviceRemoval = { definition, switches: [], coils: [], rules: [], colliders: [], banks: [], modes: [], actions: 0 }
  if (going === undefined) return none

  const wiring = wiringFor(going as EditorPart)
  const conventionSwitches = new Set(wiring.switches.map((one) => one.id))
  const conventionCoils = new Set(wiring.coils.map((one) => one.id))
  const ownedRules = new Set((wiring.rules ?? []).map((one) => one.id))

  // Devices: the part out, and out of any bank that named it.
  const banks: string[] = []
  const keptDevices = devices
    .filter((one) => idOf(one) !== deviceId)
    .map((one) => {
      const d = rec(one)
      if (d['kind'] !== 'dropTargetBank' || !list(d['targets']).includes(deviceId)) return one
      banks.push(idOf(one) ?? '?')
      return { ...d, targets: list(d['targets']).filter((t) => t !== deviceId) }
    })

  // Switches: every one that names the part as its device, and the palette's.
  const switchList = list(root['switches'])
  const goneSwitches = new Set<string>()
  for (const one of switchList) {
    const id = idOf(one)
    if (id === undefined) continue
    if (rec(one)['device'] === deviceId || conventionSwitches.has(id)) goneSwitches.add(id)
  }

  // Coils: the part's own and the palette's — unless another device still drives it.
  const ownCoil = rec(going)['coil']
  const wanted = new Set<string>(conventionCoils)
  if (typeof ownCoil === 'string') wanted.add(ownCoil)
  const stillDriven = new Set<string>()
  for (const one of keptDevices) {
    const coil = rec(one)['coil']
    if (typeof coil === 'string') stillDriven.add(coil)
  }
  const coilList = list(root['coils'])
  const goneCoils = new Set<string>()
  for (const one of coilList) {
    const id = idOf(one)
    if (id !== undefined && wanted.has(id) && !stillDriven.has(id)) goneCoils.add(id)
  }

  // Colliders that are the part's own.
  const colliderList = list(root['colliders'])
  const goneColliders = colliderList.filter((one) => rec(one)['device'] === deviceId).map((one) => idOf(one) ?? '?')

  // Actions that drive a gone coil or lock a ball in the part.
  let actions = 0
  const keepAction = (action: unknown): boolean => {
    const a = rec(action)
    const coil = rec(a['coil'])['id']
    if (typeof coil === 'string' && goneCoils.has(coil)) return false
    if (rec(a['lockBall'])['device'] === deviceId) return false
    return true
  }
  const pruned = (actionList: unknown): unknown => {
    if (!Array.isArray(actionList)) return actionList
    const kept = actionList.filter(keepAction)
    actions += actionList.length - kept.length
    return kept.length === actionList.length ? actionList : kept
  }

  // Rules: a rule its switch triggered can never fire; the palette's own go too.
  const rulesBlock = rec(root['rules'])
  const goneRules: string[] = []
  const ruleList = list(rulesBlock['rules'])
  const keptRules = ruleList.flatMap((one) => {
    const r = rec(one)
    const id = idOf(one)
    const on = r['on']
    const trigger = typeof on === 'string' && on.startsWith('switch:') ? on.slice('switch:'.length) : undefined
    if ((id !== undefined && ownedRules.has(id)) || (trigger !== undefined && goneSwitches.has(trigger))) {
      goneRules.push(id ?? '?')
      return []
    }
    const doList = pruned(r['do'])
    return [doList === r['do'] ? one : { ...r, do: doList }]
  })

  // Modes: the shots that were its switches, and the actions in their hooks.
  const modesTouched: string[] = []
  const modeList = list(rulesBlock['modes'])
  const keptModes = modeList.map((one) => {
    const m = rec(one)
    let changed = false
    const next: Rec = { ...m }
    if (Array.isArray(m['shots'])) {
      const shots = (m['shots'] as unknown[]).filter((s) => !(typeof s === 'string' && goneSwitches.has(s)))
      if (shots.length !== (m['shots'] as unknown[]).length) {
        changed = true
        modesTouched.push(idOf(one) ?? '?')
        if (shots.length === 0) delete next['shots']
        else next['shots'] = shots
      }
    }
    for (const hook of ['onStart', 'onShot', 'onComplete', 'onTimeout']) {
      if (!Array.isArray(m[hook])) continue
      const kept = pruned(m[hook])
      if (kept !== m[hook]) {
        changed = true
        next[hook] = kept
      }
    }
    return changed ? next : one
  })

  const rulesChanged =
    goneRules.length > 0 || keptRules.some((one, i) => one !== ruleList[i]) || keptModes.some((one, i) => one !== modeList[i])
  const next: Rec = { ...root, devices: keptDevices }
  if (goneSwitches.size > 0) next['switches'] = switchList.filter((one) => !goneSwitches.has(idOf(one) ?? ''))
  if (goneCoils.size > 0) next['coils'] = coilList.filter((one) => !goneCoils.has(idOf(one) ?? ''))
  if (goneColliders.length > 0) next['colliders'] = colliderList.filter((one) => rec(one)['device'] !== deviceId)
  if (rulesChanged && root['rules'] !== undefined) {
    next['rules'] = {
      ...rulesBlock,
      ...(rulesBlock['rules'] === undefined ? {} : { rules: keptRules }),
      ...(rulesBlock['modes'] === undefined ? {} : { modes: keptModes }),
    }
  }
  return {
    definition: next as unknown as MachineDefinition,
    switches: switchList.map(idOf).filter((id): id is string => id !== undefined && goneSwitches.has(id)),
    coils: coilList.map(idOf).filter((id): id is string => id !== undefined && goneCoils.has(id)),
    rules: goneRules,
    colliders: goneColliders,
    banks,
    modes: modesTouched,
    actions,
  }
}
